import AppContext from 'App/AppContext';
import { IPDefaultButton, IPDropdown, IPLabel, IPPrimaryButton } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import { getProcessingActivityRoleOptions } from 'components/ProcessingActivity/ProcessingActivityOptions';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackStylesPanelFooter,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import Joi from 'joi';
import ProcessingActivity, { ProcessingActivityRole } from 'models/processingActivity';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'utils/string';

interface IAddProcessingActivityPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAddNewProcessingActivity: (activity: ProcessingActivity, open: boolean) => void;
  onCheckCode: (code: string) => Promise<string>;
  isLoading: boolean;
}

export const AddProcessingActivityPanel = (props: IAddProcessingActivityPanelProps) => {
  const { t } = useTranslation(['translation', 'processingActivities']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [errorAsync, setErrorAsync] = useState<boolean>(false);
  const [processingActivityToAdd, setProcessingActivityToAdd] = useState<ProcessingActivity>(new ProcessingActivity());
  const appContext = useContext(AppContext);

  useEffect(() => {
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const newCode = await props.onCheckCode('');
      const newProcessingActivity = ProcessingActivity.getEmptyProcessingActivity(newCode);
      setProcessingActivityToAdd(newProcessingActivity);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const disableSave = (): boolean => {
    return !hasChanged() || error || errorAsync || isLoading || props.isLoading ? true : false;
  };

  const onSave = (editAfterSave: boolean): void => {
    if (isLoading) return;

    setIsLoading(true);
    props.onAddNewProcessingActivity(processingActivityToAdd, editAfterSave);
    setIsLoading(false);

    props.onDismiss();
  };

  const hasChanged = (): boolean => {
    if (processingActivityToAdd.name !== '') return true;

    return false;
  };

  const _localizedFields: Record<string, string> = {
    name: t('processingActivities:Panels.Add.NameLabel'),
    code: t('processingActivities:Panels.Add.CodeLabel'),
  };

  const validate = async (fieldName: string): Promise<string> => {
    if (hasChanged()) {
      const result = processingActivityToAdd.validate(_localizedFields);
      setError(result.error);
      if (result.error) {
        const fieldError = result.error.details.find((e) => {
          return e.context?.key === fieldName;
        });

        if (fieldError) {
          return fieldError.message;
        }
      }
    }

    if (fieldName === 'code' && processingActivityToAdd.code) {
      const newCode = await props.onCheckCode(processingActivityToAdd.code);
      const exists = !isEmpty(newCode);
      setErrorAsync(exists);

      if (exists) {
        return t('processingActivities:Panels.Add.CodeError');
      }
    }

    return '';
  };

  // On Render Footer: Shows Save and Cancel Button at the bottom and performs appropriate actions
  const onRenderFooterContent = () => {
    return (
      <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            disabled={disableSave()}
            onClick={() => {
              onSave(false);
              props.onDismiss();
            }}
            text={t('translation:General.Button.Save')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton
            disabled={disableSave()}
            onClick={() => {
              onSave(true);
              props.onDismiss();
            }}
            text={t('translation:General.Button.SaveAndEdit')}
          ></IPDefaultButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')}></IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  return (
    <IPPanel
      headerText={t('processingActivities:Panels.Add.Title')}
      type={IPPanelType.medium}
      isOpen={props.isOpen}
      isBlocking={true}
      isLightDismiss={true}
      layerProps={globalLayerRightSideBarProps}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      onDismiss={props.onDismiss}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <IPTextField
            required
            value={processingActivityToAdd.code}
            label={t('processingActivities:Panels.Add.CodeLabel')}
            placeholder={t('processingActivities:Panels.Add.CodePlaceholder')}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const processingActivityClone = processingActivityToAdd.clone();
              processingActivityClone.code = newValue ? newValue.trim() : '';
              setProcessingActivityToAdd(processingActivityClone);
            }}
            onGetErrorMessage={() => {
              return validate('code');
            }}
            maxLength={32}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPTextField
            required
            autoFocus={!appContext.isMobileView}
            label={t('processingActivities:Panels.Add.NameLabel')}
            placeholder={t('processingActivities:Panels.Add.NamePlaceholder')}
            value={processingActivityToAdd.name}
            onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
              const processingActivityClone = processingActivityToAdd.clone();
              processingActivityClone.name = newValue ? newValue.trimStart() : '';
              setProcessingActivityToAdd(processingActivityClone);
            }}
            onGetErrorMessage={() => {
              return validate('name');
            }}
            maxLength={512}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('processingActivities:Panels.Add.RoleInfo')}>
            <IPLabel>{t('processingActivities:Panels.Add.Role')}</IPLabel>
          </TextInfo>
          <IPDropdown
            selectedKey={processingActivityToAdd.role}
            options={getProcessingActivityRoleOptions()}
            onChange={(ev, option) => {
              const processingActivityClone = processingActivityToAdd.clone();
              processingActivityClone.role = option?.key as ProcessingActivityRole;
              setProcessingActivityToAdd(processingActivityClone);
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <AuthSchemaPicker
            selectedAuthSchemaId={processingActivityToAdd.authSchemaId}
            onSelect={(schema) => {
              const newProcessingActivityToAdd = processingActivityToAdd.clone();
              newProcessingActivityToAdd.authSchemaId = schema?.authSchemaId;
              setProcessingActivityToAdd(newProcessingActivityToAdd);
            }}
          />
        </IPStack.Item>
      </IPStack>
    </IPPanel>
  );
};
